/**
 * @fileoverview Own browser startup, shared Redux/provider initialization, and top-level diagnostics. `App.js` owns authenticated routing; `BrowserErrorBoundary.js` owns render recovery; `appEnvironment.js` supplies environment presentation. Theme/runtime initialization belongs here while product state and backend admission stay with their owning domains.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import '@fontsource-variable/ibm-plex-sans/wght.css';
import '@fontsource-variable/source-serif-4/wght.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource/ibm-plex-mono/700.css';
import './index.css';
import App from './App';
import BrowserErrorBoundary from './BrowserErrorBoundary';
import { installBrowserErrorReporting } from './browserErrorReporting';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux'
import { PrimeReactProvider } from 'primereact/api';
import store from './store/store'
import { applyEnvironmentFavicon } from './appEnvironment';

// import "primereact/resources/primereact.css";

import "primeflex/primeflex.css";
//core
import "primereact/resources/primereact.min.css";
// theme: system-aware via media queries
import "./prime-theme.css";
import './App.css'
import './design-system/surfaces.css';

const RESIZE_OBSERVER_DEV_OVERLAY_MESSAGES = new Set([
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
]);



const isResizeObserverDevOverlayError = (event) => {
  const message = event?.message || event?.error?.message;
  return RESIZE_OBSERVER_DEV_OVERLAY_MESSAGES.has(message);
};

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (isResizeObserverDevOverlayError(event)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
}

applyEnvironmentFavicon();
installBrowserErrorReporting();

ReactDOM.render(
  <Provider store={store}>
    <React.StrictMode>
      <PrimeReactProvider>
        <BrowserErrorBoundary><App /></BrowserErrorBoundary>
      </PrimeReactProvider>
    </React.StrictMode>
  </Provider>,
  document.getElementById('root')
);

reportWebVitals();

// Apply system theme class to html element and listen for changes


const applySystemThemeClass = () => {
  if (typeof window === 'undefined') return;
  const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  document.documentElement.classList.toggle('dark', Boolean(isDark));
};

applySystemThemeClass();
if (window.matchMedia) {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  if (media.addEventListener) {
    media.addEventListener('change', applySystemThemeClass);
  } else if (media.addListener) {
    media.addListener(applySystemThemeClass);
  }
}
